import { ChevronRightIcon } from 'lucide-react';
import { Link } from 'react-router';

import type { OrderDTO } from '@invenco/sales-interface/interfaces';

import { Button } from '@/components/core/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/core/card';
import { Skeleton } from '@/components/core/skeleton';
import { EMPTY_TEXT_VALUE } from '@/constants';
import { useGateways } from '@/gateways/GatewayProvider';
import { formatDate, formatMoney } from '@/shared/helpers';
import { useEntityDetailsQuery } from '@/shared/hooks/queries';

type Props = {
  orderId: string;
};

export function ReturnOrderCard({ orderId }: Props) {
  const { salesGateway } = useGateways();

  const { data: order, isLoading } = useEntityDetailsQuery<OrderDTO>({
    parentKey: 'orders',
    id: orderId,
    query: (fetchId, { signal }) => salesGateway.getOrder(fetchId, { signal }),
  });

  const stats = [
    { label: 'Order', value: order?.name },
    { label: 'Customer', value: order?.customerName },
    { label: 'Channel', value: order?.channelName },
    { label: 'Ordered', value: order ? formatDate(order.orderedAt ?? order.createdAt) : undefined },
    { label: 'Total', value: order ? formatMoney(order.totalPrice, order.currency) : undefined },
  ];

  return (
    <Card aria-label="Order">
      <CardHeader>
        <CardTitle>Order</CardTitle>
        <CardAction>
          <Button variant="ghost" asChild>
            <Link to={`/sales/orders/${orderId}`}>
              View
              <ChevronRightIcon />
            </Link>
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" aria-busy={isLoading}>
          {stats.map(({ label, value }) => (
            <div key={label} className="flex min-w-0 flex-col gap-1">
              <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
              <dd className="text-sm font-medium break-words">
                {isLoading ? <Skeleton className="h-4 w-20" /> : value || EMPTY_TEXT_VALUE}
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}
